import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, UserPlus, RefreshCw } from "lucide-react";
import api from "@/lib/api";

const inputCls = "w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-cadmium/60 transition-colors";
const labelCls = "block text-[10px] font-mono font-semibold uppercase tracking-[0.2em] text-slate-500 mb-1.5";

export const CompanyManager = () => {
  const [companies, setCompanies] = useState([]);
  const [companyName, setCompanyName] = useState("");
  const [savingCompany, setSavingCompany] = useState(false);
  const [mgrFor, setMgrFor] = useState(null);
  const [mgrForm, setMgrForm] = useState({ name: "", email: "", password: "" });
  const [savingMgr, setSavingMgr] = useState(false);
  const [smrFor, setSmrFor] = useState(null);
  const [smrForm, setSmrForm] = useState({ email: "", password: "" });
  const [savingSmr, setSavingSmr] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/training/companies");
      setCompanies(res.data);
    } catch {
      toast.error("Could not load companies");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCompany = async () => {
    if (companyName.trim().length < 2) { toast.error("Company name is required"); return; }
    setSavingCompany(true);
    try {
      await api.post("/training/companies", { name: companyName.trim() });
      toast.success(`Company "${companyName.trim()}" added`);
      setCompanyName("");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Create failed");
    } finally {
      setSavingCompany(false);
    }
  };

  const deleteCompany = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? Its employees and company admins are kept but leave the company (employees move to "No company").`)) return;
    try {
      await api.delete(`/training/companies/${id}`);
      toast.success("Company deleted — employees kept");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Delete failed");
    }
  };

  const createManager = async (companyId) => {
    setSavingMgr(true);
    try {
      const res = await api.post(`/training/companies/${companyId}/managers`, mgrForm);
      toast.success(`Company admin ${mgrForm.name} created`, {
        description: res.data.emailed
          ? `Sign-in details emailed to ${mgrForm.email}.`
          : "Account created but the credentials email could not be delivered — share the password directly.",
      });
      setMgrForm({ name: "", email: "", password: "" });
      setMgrFor(null);
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Create failed");
    } finally {
      setSavingMgr(false);
    }
  };

  const deleteManager = async (id, name) => {
    if (!window.confirm(`Remove company admin ${name}? Their login stops working immediately.`)) return;
    try {
      await api.delete(`/training/managers/${id}`);
      toast.success("Company admin removed");
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Remove failed");
    }
  };

  const saveSmr = async (companyId) => {
    setSavingSmr(true);
    try {
      await api.put(`/training/companies/${companyId}/smr`, smrForm);
      toast.success("ScheduleMyRent connection saved");
      setSmrFor(null);
      setSmrForm({ email: "", password: "" });
      load();
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Save failed");
    } finally {
      setSavingSmr(false);
    }
  };

  return (
    <div className="technical-card p-6" data-testid="company-manager">
      <p className="text-xs font-mono font-semibold uppercase tracking-[0.2em] text-slate-900 mb-1">Companies</p>
      <p className="text-[11px] text-slate-500 mb-4">
        Each company gets rollup training stats, its own company admins (they sign in to manage their employees and see scores), and an optional ScheduleMyRent connection.
      </p>
      <div className="flex gap-2 mb-4">
        <input data-testid="cm-company-name" className={inputCls} value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Add a company, e.g. brightSTAR Communities" />
        <button data-testid="cm-company-add" onClick={createCompany} disabled={savingCompany} className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-cadmium px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60">
          {savingCompany ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />} Add
        </button>
      </div>
      {companies.length === 0 && <p className="text-sm text-slate-400" data-testid="cm-empty">No companies yet — add one above.</p>}
      <div className="space-y-2">
        {companies.map((c) => (
          <div key={c.id} className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`cm-company-${c.id}`}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span className="text-sm font-medium text-slate-900">{c.name}</span>
              <span className="text-[10px] font-mono text-slate-500">{c.employees} employee(s)</span>
              <span className="text-[10px] font-mono text-amber-700">{c.open_courses} open</span>
              <span className="text-[10px] font-mono text-green-700">{c.completed_courses} completed</span>
              <span className="text-[10px] font-mono text-slate-500">avg {c.avg_score != null ? `${c.avg_score}%` : "—"}</span>
              <span className="ml-auto flex items-center gap-3">
                <button onClick={() => { setMgrFor(mgrFor === c.id ? null : c.id); setMgrForm({ name: "", email: "", password: "" }); }} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-sky-600 transition-colors" data-testid={`cm-add-mgr-${c.id}`}>
                  <UserPlus size={11} /> Add admin
                </button>
                <button onClick={() => { setSmrFor(smrFor === c.id ? null : c.id); setSmrForm({ email: "", password: "" }); }} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-sky-600 transition-colors" data-testid={`cm-smr-${c.id}`}>
                  <RefreshCw size={11} /> SMR
                </button>
                <button onClick={() => deleteCompany(c.id, c.name)} className="text-slate-400 hover:text-red-600 transition-colors" aria-label={`Delete ${c.name}`} data-testid={`cm-delete-${c.id}`}><Trash2 size={12} /></button>
              </span>
            </div>
            {(c.managers || []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {c.managers.map((mg) => (
                  <span key={mg.id} className="inline-flex items-center gap-1.5 rounded-sm border border-cadmium/30 bg-cadmium/10 px-2 py-0.5 text-[10px] font-mono text-sky-600" data-testid={`cm-mgr-${mg.id}`}>
                    {mg.name} · {mg.email}
                    <button onClick={() => deleteManager(mg.id, mg.name)} className="hover:text-red-600 transition-colors" aria-label={`Remove ${mg.name}`} data-testid={`cm-mgr-delete-${mg.id}`}><Trash2 size={10} /></button>
                  </span>
                ))}
              </div>
            )}
            {mgrFor === c.id && (
              <div className="mt-3 grid sm:grid-cols-4 gap-2 border-t border-slate-200 pt-3" data-testid={`cm-mgr-form-${c.id}`}>
                <input className={inputCls} placeholder="Admin name *" value={mgrForm.name} onChange={(e) => setMgrForm({ ...mgrForm, name: e.target.value })} data-testid={`cm-mgr-name-${c.id}`} />
                <input className={inputCls} type="email" placeholder="Admin email *" value={mgrForm.email} onChange={(e) => setMgrForm({ ...mgrForm, email: e.target.value })} data-testid={`cm-mgr-email-${c.id}`} />
                <input className={inputCls} placeholder="Temporary password *" value={mgrForm.password} onChange={(e) => setMgrForm({ ...mgrForm, password: e.target.value })} data-testid={`cm-mgr-password-${c.id}`} />
                <button onClick={() => createManager(c.id)} disabled={savingMgr} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-cadmium px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60" data-testid={`cm-mgr-save-${c.id}`}>
                  {savingMgr ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />} Create
                </button>
              </div>
            )}
            {smrFor === c.id && (
              <div className="mt-3 border-t border-slate-200 pt-3" data-testid={`cm-smr-form-${c.id}`}>
                <label className={labelCls}>ScheduleMyRent credentials for {c.name}</label>
                <div className="grid sm:grid-cols-3 gap-2">
                  <input className={inputCls} type="email" placeholder="SMR email *" value={smrForm.email} onChange={(e) => setSmrForm({ ...smrForm, email: e.target.value })} data-testid={`cm-smr-email-${c.id}`} />
                  <input className={inputCls} type="password" placeholder="SMR password *" value={smrForm.password} onChange={(e) => setSmrForm({ ...smrForm, password: e.target.value })} data-testid={`cm-smr-password-${c.id}`} />
                  <button onClick={() => saveSmr(c.id)} disabled={savingSmr} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-cadmium px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-cadmium-hover transition-colors disabled:opacity-60" data-testid={`cm-smr-save-${c.id}`}>
                    {savingSmr ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Save connection
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
